import { Suspense, useEffect, useRef } from "react";
import { useLocation, useRoutes } from "react-router-dom";
import { ErrorBoundary } from "./components/error-boundary/ErrorBoundary";
import { FeedbackForm } from "./components/feedback-form/FeedbackForm";
import { Header } from "./einstein/components/common/header/header";
import { useAppDispatch, useAppSelector } from "./lib/store/dynamicStore";
import { useFeatureFlagStore } from "./lib/store/featureFlagStore";
import { initializeAuth } from "./lib/store/features/auth/authSlice";
import { buildRoutes } from "./routes/index";
import { FullPageLoader } from "./shared/pageLoader";

function App() {
	const dispatch = useAppDispatch();
	const didStartAuthInitialization = useRef(false);
	const { isLoading, isAuthenticated } = useAppSelector((state) => state.auth);
	const flags = useFeatureFlagStore((state) => state.flags);
	const fetchFeatureFlags = useFeatureFlagStore(
		(state) => state.fetchFeatureFlags,
	);
	const element = useRoutes(buildRoutes(flags));
	const location = useLocation();

	useEffect(() => {
		if (!didStartAuthInitialization.current) {
			didStartAuthInitialization.current = true;
			// Always initialize auth to handle MSAL redirects
			dispatch(initializeAuth());
		}

		const deploymentEnv = import.meta.env.VITE_CUSTOM_ENV_NAME;
		if (deploymentEnv === "production") {
			document.title = "Einstein";
		} else {
			document.title = "Einstein - Development";
		}
	}, [dispatch]);

	useEffect(() => {
		if (isAuthenticated) {
			fetchFeatureFlags();
		}
	}, [isAuthenticated, fetchFeatureFlags]);

	const isLoginPage = location.pathname === "/login";

	// Don't show loader on login page
	if (isLoading && !isLoginPage) {
		return (
			<FullPageLoader
				className="min-h-screen"
				message="Preparing Einstein"
				ariaLabel="Checking authentication status"
			/>
		);
	}

	return (
		<>
			{!isLoginPage && isAuthenticated && <Header />}
			{isAuthenticated && <FeedbackForm />}
			<ErrorBoundary name="AppRouteBoundary">
				<Suspense
					fallback={
						<FullPageLoader
							className="min-h-screen"
							message="Loading workspace"
							ariaLabel="Loading page"
						/>
					}
				>
					{element}
				</Suspense>
			</ErrorBoundary>
		</>
	);
}

export default App;
